import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Checkbox } from "@/components/ui/checkbox";
import { PromoPicker } from "@/components/PromoPicker";
import { Loader2, ChevronRight } from "lucide-react";
import { motion } from "framer-motion";
import { useI18n } from "@/context/I18nContext";

const fixLabel = (s) => (s || "").replace(/diagonal\b/gi, "diagonale");
const isIslandais = (s) => (s || "").toLowerCase().includes("island");

export default function ModelSelection() {
  const { t } = useI18n();
  const [ranges, setRanges] = useState([]);
  const [rangeKey, setRangeKey] = useState(null);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/ranges").then(({ data }) => {
      setRanges(data);
      if (data[0]) setRangeKey(data[0].key);
    }).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!rangeKey) return;
    setGroups([]);
    api.get(`/menu/${rangeKey}`).then(({ data }) => setGroups(data.groups || []));
  }, [rangeKey]);

  const [sizeFilter, setSizeFilterState] = useState(() => {
    try { return JSON.parse(localStorage.getItem("fautras_size")) || { std: true, isl: false }; } catch { return { std: true, isl: false }; }
  });
  const setSizeFilter = (updater) => setSizeFilterState((s) => {
    const n = typeof updater === "function" ? updater(s) : updater;
    localStorage.setItem("fautras_size", JSON.stringify(n));
    return n;
  });
  const anyIsl = useMemo(() => groups.some((g) => (g.entries || []).some((e) => isIslandais(e.size))), [groups]);
  const matchSize = (g) =>
    (sizeFilter.std && (g.entries || []).some((e) => !isIslandais(e.size))) ||
    (sizeFilter.isl && (g.entries || []).some((e) => isIslandais(e.size)));
  const shownGroups = rangeKey === "van"
    ? groups.filter((g) => matchSize(g))
    : groups;

  const countryCode = localStorage.getItem("fautras_country") || "FR";
  const [promos, setPromos] = useState([]);
  const [, setPromoTick] = useState(0);
  useEffect(() => {
    if (!rangeKey) { setPromos([]); return; }
    api.get(`/promotions/active`, { params: { country: countryCode, range_key: rangeKey } })
      .then(({ data }) => setPromos(data || [])).catch(() => setPromos([]));
  }, [rangeKey, countryCode]);

  if (loading) {
    return <div className="flex items-center justify-center py-32"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-5">
      <PromoPicker promos={promos} onChange={() => setPromoTick((x) => x + 1)} testid="select-promo" />
      <div className="text-center max-w-2xl mx-auto mb-5">
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">{t("Choisissez votre modèle")}</h1>
      </div>

      <div className="flex flex-col items-center mb-5">
        <div className="inline-flex rounded-lg border border-border bg-card p-1">
          {ranges.map((r) => (
            <button
              key={r.key}
              data-testid={`select-range-${r.key}`}
              onClick={() => (r.key === "occasion" ? nav("/configurer?range=occasion") : setRangeKey(r.key))}
              className={`px-5 py-2 text-sm font-semibold rounded-md transition-colors ${
                rangeKey === r.key ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {t(r.name)}
            </button>
          ))}
        </div>
        <p className="text-muted-foreground mt-3 text-center">
          {rangeKey === "van"
            ? t("Combien de chevaux souhaitez-vous transporter ?")
            : t("Quelle longueur de chargement vous faut-il ?")}
        </p>
      </div>

      {rangeKey === "van" && anyIsl && (
        <div className="flex flex-wrap items-end justify-center gap-16 mb-5" data-testid="size-filter">
          <label className="flex items-end gap-2 text-sm cursor-pointer" data-testid="size-std">
            <Checkbox checked={sizeFilter.std} onCheckedChange={(v) => setSizeFilter((s) => ({ ...s, std: !!v }))} />
            <img src="/assets/cheval-standard.png" alt="" className="max-h-9 w-auto" />
            {t("Chevaux standard")}
          </label>
          <label className="flex items-end gap-2 text-sm cursor-pointer" data-testid="size-isl">
            <Checkbox checked={sizeFilter.isl} onCheckedChange={(v) => setSizeFilter((s) => ({ ...s, isl: !!v }))} />
            <img src="/assets/cheval-islandais.png" alt="" className="max-h-6 w-auto" />
            {t("Chevaux islandais")}
          </label>
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
        {shownGroups.map((g, i) => {
          const n = rangeKey === "van"
            ? g.entries.filter((e) => (isIslandais(e.size) ? sizeFilter.isl : sizeFilter.std)).length
            : g.entries.length;
          return (
            <motion.button
              key={g.key}
              data-testid={`group-${g.key}`}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: i * 0.05 }}
              disabled={n === 0}
              onClick={() => nav(`/modeles?range=${rangeKey}&group=${g.key}`)}
              className={`group text-left rounded-2xl border bg-card p-4 transition-all ${
                n === 0 ? "opacity-40 cursor-not-allowed border-border"
                : "border-border hover:border-primary hover:-translate-y-1 hover:shadow-lg hover:shadow-primary/10"
              }`}
            >
              <div className="rounded-xl flex items-center justify-center overflow-hidden bg-white p-2 h-24">
                {g.image && <img src={g.image} alt={g.label} className={rangeKey === "van" ? "w-[80px] max-w-none rotate-90" : "max-h-full w-auto object-contain"} />}
              </div>
              <div className="mt-3 flex items-end justify-between">
                <div className="font-display text-xl font-bold">{t(fixLabel(g.label))}</div>
                <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-all" />
              </div>
              <div className="mt-1.5 text-xs text-muted-foreground">
                {n} {t(n > 1 ? "choix disponibles" : "choix disponible")}
              </div>
            </motion.button>
          );
        })}
      </div>
    </div>
  );
}
